import clsx from "clsx";
import style from "./EmploymentDetails.module.css";
import { RootState } from "@/store/dtm";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { set, useForm } from "react-hook-form";
import { applicationCategory } from "@/types/applicationType";
import { selectPropertyInfo, setMortgageBalance, setPropertyValue, setPurchasePrice } from "@/slices/propertyInfoSlice";
import CurrencyInput from "react-currency-input-field";
import { setAmortizationLength, setApplicationCategory, setDownPayment, setTermLength, setTotalLoan } from "@/slices/applicationSlice";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PropertyForCreationDto, SubjectPropertyDto, AddressDto } from "@/lib/DTMWebAPIClient.generated";
import { createApiClient } from "@/lib/Requests";
import dayjs from "dayjs";
import { selectPropertyAddress } from "@/slices/propertyAddressSlice";
import { AnimatePresence, motion } from "framer-motion";

export default function MortgageDetails(props: any) {
    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });
    const propertyInfo = useSelector(selectPropertyInfo).propertyInfo;
    const propertyAddress = useSelector(selectPropertyAddress).address;
    const dispatch = useDispatch();
    const [showEdit, setShowEdit] = useState(false);
    const [purchasePriceAmount, setPurchasePriceAmount] = useState("");
    const [newPurchasePriceAmount, setNewPurchasePriceAmount] = useState("");
    const [downPaymentAmount, setDownPaymentAmount] = useState("");
    const [newDownPaymentAmount, setNewDownPaymentAmount] = useState("");

    const [currentValueAmount, setCurrentValueAmount] = useState("");
    const [newCurrentValueAmount, setNewCurrentValueAmount] = useState("");
    const [remainingBalance, setRemainingBalance] = useState("");
    const [newRemainingBalance, setNewRemainingBalance] = useState("");
    const [totalLoanAmount, setTotalLoanAmount] = useState("");
    const [newTotalLoanAmount, setNewTotalLoanAmount] = useState("");

    const [applicationPurpose, setApplicationPurpose] = useState(0);
    const [applicationPurposeText, setApplicationPurposeText] = useState("");
    const [isSaving, setIsSaving] = useState(false);


    const openEdit = () => {
        setShowEdit(true);
        props.editModeOn(!showEdit);
        const formattedPurchasePrice = propertyInfo.purchasePrice.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        methods.setValue("purchasePrice", propertyInfo ? propertyInfo.purchasePrice == 0 ? "" : formattedPurchasePrice : "");
        setNewPurchasePriceAmount(propertyInfo ? propertyInfo.purchasePrice == 0 ? "" : propertyInfo.purchasePrice.toString() : "");

        const formattedPropertyValue = propertyInfo.propertyValue.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        methods.setValue("currentPropertyValue", propertyInfo ? propertyInfo.propertyValue == 0 ? "" : formattedPropertyValue : "");
        setNewCurrentValueAmount(propertyInfo ? propertyInfo.propertyValue == 0 ? "" : propertyInfo.propertyValue.toString() : "");

        const formattedBalance = propertyInfo.mortgageBalance.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        methods.setValue("remainingBalance", propertyInfo ? propertyInfo.propertyValue == 0 ? "" : formattedBalance : "");
        setNewRemainingBalance(propertyInfo ? propertyInfo.mortgageBalance == 0 ? "" : propertyInfo.mortgageBalance.toString() : "");

        const formattedDownPayment = application.downPayment.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        methods.setValue("downPayment", application ? application.downPayment == 0 ? "" : formattedDownPayment : "");
        setNewDownPaymentAmount(application ? application.downPayment == 0 ? "" : application.downPayment.toString() : "");

        const formattedTotalLoan = application.applicationCategory == applicationCategory.Buy ? (propertyInfo.purchasePrice - application.downPayment).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (application.totalLoan).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        methods.setValue("totalLoan", application ? application.totalLoan == 0 ? "" : formattedTotalLoan : "");
        setNewTotalLoanAmount(application ? application.totalLoan == 0 ? "" : application.totalLoan.toString() : "");
    };

    const closeEdit = () => {
        setShowEdit(false);
        props.editModeOn(!showEdit);
    };

    const onSubmit = async (data: any) => {
        switch (+data.applicationPurpose) {
            case 1:
                dispatch(setApplicationCategory(applicationCategory.Buy));
                setApplicationPurposeText("Buy a property");
                break;
            case 2:
                dispatch(setApplicationCategory(applicationCategory.Refinance));
                setApplicationPurposeText("Refinance my mortgage");
                break;
            case 3:
                dispatch(setApplicationCategory(applicationCategory.Transfer));
                setApplicationPurposeText("Transfer my mortgage");
                break;
        }
        dispatch(setTermLength(data.term != "unknown" ? +data.term : "unknown"));
        dispatch(setAmortizationLength(data.amortization != "unknown" ? +data.amortization : "unknown"));
        setPurchasePriceAmount(data.purchasePrice);
        dispatch(setPurchasePrice(+newPurchasePriceAmount));
        dispatch(setDownPayment(+newDownPaymentAmount));
        setCurrentValueAmount(data.currentPropertyValue);
        dispatch(setPropertyValue(+newCurrentValueAmount));
        setRemainingBalance(data.remainingBalance);
        dispatch(setMortgageBalance(+newRemainingBalance));
        setTotalLoanAmount(data.totalLoan);
        dispatch(setTotalLoan(+newTotalLoanAmount));

        setIsSaving(true);
        let closingDate = undefined;
        let financingConditionDate = undefined;
        let fundingDate = undefined;
        if (propertyInfo.closingDate && propertyInfo.closingDate.trim() != "") {
            let dateComponents = propertyInfo.closingDate.split("/");
            closingDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
        }
        if (propertyInfo.financingConditionDate && propertyInfo.financingConditionDate.trim() != "") {
            let dateComponents = propertyInfo.financingConditionDate.split("/");
            financingConditionDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
        }
        if (propertyInfo.fundingDate && propertyInfo.fundingDate.trim() != "") {
            let dateComponents = propertyInfo.fundingDate.split("/");
            fundingDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
        }
        await client.apiDealsApplicationsMortgagesSubjectProperty(new PropertyForCreationDto({
            applicationId: 0,
            remainingBalanceAmount: +newRemainingBalance,
            downPaymentAmount: +newDownPaymentAmount,
            totalLoanAmount: application.applicationCategory == applicationCategory.Buy ? +newPurchasePriceAmount - +newDownPaymentAmount : +newTotalLoanAmount,
            newMoneyRequestedAmount: 0,
            subjectProperty: new SubjectPropertyDto({
                originalPurchasePrice: propertyInfo.originalPurchasePrice,
                purchasePrice: application.applicationCategory == applicationCategory.Buy ? +newPurchasePriceAmount : +newCurrentValueAmount,
                propertySalePrice: 0,
                address: new AddressDto({
                    id: 0,
                    streetNumber: propertyAddress.streetNumber,
                    streetName: propertyAddress.streetName,
                    streetType: propertyAddress.streetType,
                    streetDirection: propertyAddress.streetDirection,
                    unitType: propertyAddress.unitType,
                    unitNumber: propertyAddress.unitNumber,
                    city: propertyAddress.city,
                    region: propertyAddress.province,
                    postalCode: propertyAddress.postalCode,
                    country: propertyAddress.country,
                    timeAtResidenceInMonths: 0,
                    residenceStatusType_Name: "",
                    deliveryMode: undefined,
                    modeNumber: undefined,
                    deliveryMode2: undefined,
                    modeNumber2: undefined,
                    isMailing: false
                }),
                propertyTax: propertyInfo.propertyTax,
                propertyTaxFrequency: "annually",
                mortgageHolder: propertyInfo.mortgageHolder,
                livableArea: propertyInfo.livableArea,
                livableAreaType: propertyInfo.livableAreaUnit,
                heatingCost: propertyInfo.heatingCost,
                heatingType: propertyInfo.heatingType,
                heatingCostFrequency: "monthly",
                condoFees: propertyInfo.maintenanceFee,
                condoFeesFrequency: "monthly",
                futureStatusOfProperty: "N",
                occupancyType: propertyInfo.purposeOfProperty,
                subjectPropertyRentalUnits: propertyInfo.subjectPropertyRentalUnits,
                totalRentalUnits: propertyInfo.numOfRentalUnits,
                totalRentalProperties: propertyInfo.numOfRentalProperties,
                ownershipPercentage: propertyInfo.ownershipPercentage,
                hasCurrentRentalProperties: propertyInfo.ownOtherRentalProperties,
                isPlanningOnRenting: propertyInfo.rentOut,
                isSeparateLegalUnit: propertyInfo.separateUnit,
                rent: propertyInfo.monthlyRent,
                estimatedFundingDate: fundingDate,
                closingDate: closingDate,
                conditionOfFinancingDate: financingConditionDate,
                rentalIncome: propertyInfo.monthlyRent
            })
        })).then((result) => {

            console.log(result);
            setIsSaving(false);
            closeEdit();

        }).catch((error) => {
            setIsSaving(false);
            console.log("Error saving property information", error);
        });
    };

    const onError = (errors: object) => {
        console.log(errors);
    }

    const methods = useForm({
        defaultValues: {
            applicationPurpose: "",
            applicationType: "",
            term: "",
            amortization: "",
            purchasePrice: "",
            downPayment: "",
            currentPropertyValue: "",
            remainingBalance: "",
            totalLoan: ""
        },
    });

    useEffect(() => {
        const formattedPurchasePrice = propertyInfo.purchasePrice.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        setPurchasePriceAmount(formattedPurchasePrice);
        methods.setValue("purchasePrice", propertyInfo ? propertyInfo.purchasePrice == 0 ? "" : formattedPurchasePrice : "");

        const formattedDownPayment = application.downPayment.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        setDownPaymentAmount(formattedDownPayment);
        methods.setValue("downPayment", application ? application.downPayment == 0 ? "" : formattedDownPayment : "");

        const formattedPropertyValue = propertyInfo.propertyValue.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        setCurrentValueAmount(formattedPropertyValue);
        methods.setValue("currentPropertyValue", propertyInfo ? propertyInfo.propertyValue == 0 ? "" : formattedPropertyValue : "");

        const formattedBalance = propertyInfo.mortgageBalance.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        setRemainingBalance(formattedBalance);
        methods.setValue("remainingBalance", propertyInfo ? propertyInfo.mortgageBalance == 0 ? "" : formattedBalance : "");

        const formattedTotalLoan = application.applicationCategory == applicationCategory.Buy ? (propertyInfo.purchasePrice - application.downPayment).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (application.totalLoan).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        setTotalLoanAmount(formattedTotalLoan);

        methods.setValue("applicationPurpose", application.applicationCategory == applicationCategory.Buy ? "1" : application.applicationCategory == applicationCategory.Refinance ? "2" : "3");
        methods.setValue("applicationType", application.searchingForProperty ? "true" : "false");
        methods.setValue("term", application ? application.termLength == 0 ? "" : application.termLength.toString() : "");
        methods.setValue("amortization", application ? application.amortizationLength == 0 ? "" : application.amortizationLength.toString() : "");
        methods.setValue("totalLoan", application ? application.totalLoan == 0 ? "" : formattedTotalLoan : "");
        switch (application.applicationCategory) {

            case applicationCategory.Buy:
                setApplicationPurposeText("Buy a property");
                setApplicationPurpose(1);
                break;
            case applicationCategory.Refinance:
                setApplicationPurposeText("Refinance my mortgage");
                setApplicationPurpose(2);
                break;
            case applicationCategory.Transfer:
                setApplicationPurposeText("Transfer my mortgage");
                setApplicationPurpose(3);
                break;
        }
    }, []);

    useEffect(() => {
        const formattedPurchasePrice = propertyInfo.purchasePrice.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        setPurchasePriceAmount(formattedPurchasePrice);

        const formattedPropertyValue = propertyInfo.propertyValue.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        setCurrentValueAmount(formattedPropertyValue);
    }, [propertyInfo]);

    useEffect(() => {
        const formattedDownPayment = application.downPayment.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        setDownPaymentAmount(formattedDownPayment);
    }, [application]);

    return (
        <div className="mt-5">
            <div className="flex flex-row justify-between mt-5">
                <h2>Mortgage Details</h2>
                {!showEdit && !props.restrictEditing && (
                    <h3
                        className="brand-blue-text text-xs grid grid-flow-col cursor-pointer"
                        onClick={openEdit}
                    >
                        <img
                            src="assets/edit-icon.svg"
                            className={style.editIcon}
                            alt="edit"
                        />
                        <span>Edit</span>
                    </h3>
                )}
            </div>

            <AnimatePresence mode="wait" initial={false}>
                {!showEdit ?
                    <motion.div
                        key="0"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}>
                        <div className="flex md:flex-row flex-col">
                            <div className={style.twoColFirst}>
                                <p className="mt-5 text-sm">Application Purpose</p>
                                <h3 className="mt-3">{applicationPurposeText}</h3>
                            </div>
                            {application.applicationCategory == applicationCategory.Buy &&
                                <div>
                                    <p className="mt-5 text-sm">Application Type</p>
                                    <h3 className="mt-3">{application?.searchingForProperty ? "Mortgage Pre-Approval" : "Mortgage Approval"}</h3>
                                </div>
                            }
                        </div>
                        <div className="flex md:flex-row flex-col">
                            <div className={style.twoColFirst}>
                                <p className="mt-5 text-sm">Term</p>
                                <h3 className="mt-3">
                                    {application?.termLength == 0.5 ? "6 Months" :
                                        application?.termLength == 1 ? "1 Year" :
                                            application?.termLength == "unknown" ? "I don't know" :
                                                application?.termLength + " Years"} </h3>
                            </div>
                            <div>
                                <p className="mt-5 text-sm">Amortization</p>
                                <h3 className="mt-3">
                                    {application?.amortizationLength == 1 ? "1 Year" :
                                        application?.amortizationLength == "unknown" ? "I don't know" :
                                            application?.amortizationLength + " Years"} </h3>
                            </div>
                        </div>
                        {application.applicationCategory == applicationCategory.Buy &&
                            <div className="flex md:flex-row flex-col">
                                <div className={style.twoColFirst}>
                                    <p className="mt-5 text-sm">Estimated Purchase Price</p>
                                    <h3 className="mt-3">{purchasePriceAmount}</h3>
                                </div>
                                <div>
                                    <p className="mt-5 text-sm">Estimated Down Payment</p>
                                    <h3 className="mt-3">{downPaymentAmount}</h3>
                                </div>
                            </div>
                        }
                        {(application.applicationCategory == applicationCategory.Refinance || application.applicationCategory == applicationCategory.Transfer) &&
                            <div className="flex md:flex-row flex-col">
                                <div className={style.twoColFirst}>
                                    <p className="mt-5 text-sm">Estimated property value</p>
                                    <h3 className="mt-3">{currentValueAmount}</h3>
                                </div>
                                <div>
                                    <p className="mt-5 text-sm">Current balance</p>
                                    <h3 className="mt-3">{remainingBalance}</h3>
                                </div>
                            </div>
                        }
                        {(application.applicationCategory == applicationCategory.Refinance) &&
                            <div className="flex md:flex-row flex-col">
                                <div className={style.twoColFirst}>
                                    <p className="mt-5 text-sm">Total loan amount</p>
                                    <h3 className="mt-3">{totalLoanAmount}</h3>
                                </div>
                            </div>
                        }
                    </motion.div>
                    :
                    <motion.div
                        key="1"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}>
                        <form onSubmit={methods.handleSubmit(onSubmit, onError)}>
                            {applicationPurpose == 1 && (<div className="flex md:flex-row flex-col">
                                <div className={style.twoColFirst}>
                                    <p className="mt-5 text-sm">Estimated Purchase Price</p>
                                    <div className="flex relative">
                                        <span>
                                            <img
                                                src="assets/error-icon.svg"
                                                className={clsx(
                                                    methods.formState?.errors?.purchasePrice
                                                        ? ""
                                                        : "hidden",
                                                    "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                )}
                                                alt="error"
                                                height={5}
                                                width={5}
                                            />
                                        </span>
                                        <CurrencyInput
                                            id="purchasePrice"
                                            placeholder="$"
                                            prefix={"$"}
                                            decimalsLimit={2}
                                            decimalScale={2}
                                            allowNegativeValue={false}
                                            maxLength={10}
                                            value={newPurchasePriceAmount}
                                            className={clsx(
                                                methods.formState?.errors?.purchasePrice?.message ? "error" : "",
                                                "mt-2 w:[220px] md:w-[305px]"
                                            )}
                                            {...methods.register("purchasePrice", {
                                                required: "Enter the purchase price of the property.",
                                            })}
                                            onValueChange={(value, name, values) => {
                                                value != undefined ? setNewPurchasePriceAmount(value) : setNewPurchasePriceAmount("")
                                            }}
                                        />
                                    </div>
                                    {methods.formState?.errors?.purchasePrice?.message && (
                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                            {methods.formState.errors.purchasePrice.message}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <p className="mt-5 text-sm">Estimated Down Payment</p>
                                    <div className="flex relative">
                                        <span>
                                            <img
                                                src="assets/error-icon.svg"
                                                className={clsx(
                                                    methods.formState?.errors?.purchasePrice
                                                        ? ""
                                                        : "hidden",
                                                    "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                )}
                                                alt="error"
                                                height={5}
                                                width={5}
                                            />
                                        </span>
                                        <CurrencyInput
                                            id="downPayment"
                                            placeholder="$"
                                            prefix={"$"}
                                            decimalsLimit={2}
                                            decimalScale={2}
                                            allowNegativeValue={false}
                                            maxLength={10}
                                            value={newDownPaymentAmount}
                                            className={clsx(
                                                methods.formState?.errors?.downPayment?.message ? "error" : "",
                                                "mt-2 w:[220px] md:w-[305px]"
                                            )}
                                            {...methods.register("downPayment", {
                                                required: "Enter an amount of the down payment.",
                                            })}
                                            onValueChange={(value, name, values) => value != undefined ? setNewDownPaymentAmount(value) : setNewDownPaymentAmount("")}
                                        />
                                    </div>
                                    {methods.formState?.errors?.downPayment?.message && (
                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                            {methods.formState.errors.downPayment.message}
                                        </span>
                                    )}
                                </div>
                            </div>
                            )}

                            {applicationPurpose != 1 &&
                            <>
                            <div className="flex md:flex-row flex-col">
                                <div className={style.twoColFirst}>
                                    <p className="mt-5 text-sm">Estimated property value</p>
                                    <div className="flex relative">
                                        <span>
                                            <img
                                                src="assets/error-icon.svg"
                                                className={clsx(
                                                    methods.formState?.errors?.currentPropertyValue
                                                        ? ""
                                                        : "hidden",
                                                    "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                )}
                                                alt="error"
                                                height={5}
                                                width={5}
                                            />
                                        </span>
                                        <CurrencyInput
                                            id="currentValue"
                                            placeholder="$"
                                            prefix={"$"}
                                            decimalsLimit={2}
                                            decimalScale={2}
                                            allowNegativeValue={false}
                                            maxLength={10}
                                            value={newCurrentValueAmount}
                                            className={clsx(
                                                methods.formState?.errors?.currentPropertyValue?.message ? "error" : "",
                                                "mt-2 w:[220px] md:w-[305px]"
                                            )}
                                            {...methods.register("currentPropertyValue", {
                                                required: "Enter the current value of your property.",
                                            })}
                                            onValueChange={(value, name, values) => {
                                                value != undefined ? setNewCurrentValueAmount(value) : setNewCurrentValueAmount("")
                                            }}
                                        />
                                    </div>
                                    {methods.formState?.errors?.currentPropertyValue?.message && (
                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                            {methods.formState.errors.currentPropertyValue.message}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <p className="mt-5 text-sm">Current balance</p>
                                    <div className="flex relative">
                                        <span>
                                            <img
                                                src="assets/error-icon.svg"
                                                className={clsx(
                                                    methods.formState?.errors?.remainingBalance
                                                        ? ""
                                                        : "hidden",
                                                    "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                )}
                                                alt="error"
                                                height={5}
                                                width={5}
                                            />
                                        </span>
                                        <CurrencyInput
                                            id="remainingBalance"
                                            placeholder="$"
                                            prefix={"$"}
                                            decimalsLimit={2}
                                            decimalScale={2}
                                            allowNegativeValue={false}
                                            maxLength={10}
                                            value={newRemainingBalance}
                                            className={clsx(
                                                methods.formState?.errors?.remainingBalance?.message ? "error" : "",
                                                "mt-2 w:[220px] md:w-[305px]"
                                            )}
                                            {...methods.register("remainingBalance", {
                                                required: "Enter the remaining balance of your mortgage.",
                                            })}
                                            onValueChange={(value, name, values) => value != undefined ? setNewRemainingBalance(value) : setNewRemainingBalance("")}
                                        />
                                    </div>
                                    {methods.formState?.errors?.remainingBalance?.message && (
                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                            {methods.formState.errors.remainingBalance.message}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex md:flex-row flex-col">
                                <div className={style.twoColFirst}>
                                    <p className="mt-5 text-sm">Total loan amount</p>
                                    <div className="flex relative">
                                        <span>
                                            <img
                                                src="assets/error-icon.svg"
                                                className={clsx(
                                                    methods.formState?.errors?.totalLoan
                                                        ? ""
                                                        : "hidden",
                                                    "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                )}
                                                alt="error"
                                                height={5}
                                                width={5}
                                            />
                                        </span>
                                        <CurrencyInput
                                            id="totalLoan"
                                            placeholder="$"
                                            prefix={"$"}
                                            decimalsLimit={2}
                                            decimalScale={2}
                                            allowNegativeValue={false}
                                            maxLength={10}
                                            value={newTotalLoanAmount}
                                            className={clsx(
                                                methods.formState?.errors?.totalLoan?.message ? "error" : "",
                                                "mt-2 w:[220px] md:w-[305px]"
                                            )}
                                            {...methods.register("totalLoan", {
                                                required: "Enter the total amount you want to borrow.",
                                            })}
                                            onValueChange={(value, name, values) => {
                                                value != undefined ? setNewTotalLoanAmount(value) : setNewTotalLoanAmount("")
                                            }}
                                        />
                                    </div>
                                    {methods.formState?.errors?.totalLoan?.message && (
                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                            {methods.formState.errors.totalLoan.message}
                                        </span>
                                    )}
                                </div>
                            </div>
                            </>
                            }

                            <div className="flex max-sm:hidden">
                                <div className="mb-5">
                                    <button
                                        className="primary mt-5 w-169 sm:w-28 h-11 px-6"
                                        type="button"
                                        onClick={closeEdit}
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <div className="mb-5">
                                    <button
                                        type="submit"
                                        className="primary mt-5 w-169 sm:w-28 h-11 px-6 mx-5"
                                        disabled={isSaving}
                                    >
                                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                        Save
                                    </button>
                                </div>
                            </div>
                            <div className="flex sm:hidden">
                                <div className="mb-5">
                                    <button
                                        className="primary mt-5 w-169 sm:w-28 h-11 px-6"
                                        type="button"
                                        onClick={closeEdit}
                                        style={{ minWidth: 90 }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <div className="mb-5">
                                    <button
                                        type="submit"
                                        className="primary mt-5 w-169 sm:w-28 h-11 px-6 mx-5"
                                        disabled={isSaving}
                                        style={{ minWidth: 90 }}
                                    >
                                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                        Save
                                    </button>
                                </div>
                            </div>
                        </form>
                    </motion.div>
                }
            </AnimatePresence>
        </div>
    );
}